import { useEffect, useMemo, useState } from 'react';
import { Screen } from '../components/AppShell';
import {
  Badge,
  EmptyState,
  Field,
  Icon,
  RegPlate,
  SectionHeader,
  Skeleton,
  StatusPill,
  Switch,
  Sheet,
} from '../components/ui';
import { api } from '../lib/api';
import { useAction, useLoad } from '../lib/hooks';
import { useAuth } from '../store/auth';
import { daysBetween, formatDate, today } from '../../../shared/schedule';
import './screens.css';

/* ------------------------------------------------------------------ types */

interface Settings {
  enabled: boolean;
  leadDays: number[];
  types: string[];
  recipients: string[];
  copyCustomer: boolean;
  weeklyDigest: boolean;
}

interface SettingsResponse {
  settings: Settings;
  availableTypes: { key: string; label: string }[];
  emailReady: boolean;
  from: string;
}

interface CustomReminder {
  id: string;
  asset_id: string | null;
  title: string;
  notes: string | null;
  due_date: string;
  lead_days: number[] | null;
  recipients: string[] | null;
  done_at: string | null;
  reg_no: string | null;
  fleet_no: string | null;
  asset_kind: 'vehicle' | 'trailer' | null;
}

interface SendRow {
  ref: string;
  kind: string;
  recipients: string;
  subject: string;
  ok: number;
  error: string | null;
  sent_at: string;
}

/* ------------------------------------------------------------------ utils */

/** Lead times are stored in days but nobody thinks in days past a fortnight. */
function leadLabel(days: number): string {
  if (days === 0) return 'On the day';
  if (days === 1) return '1 day before';
  if (days % 7 === 0 && days >= 7) {
    const weeks = days / 7;
    return `${weeks} week${weeks === 1 ? '' : 's'} before`;
  }
  return `${days} days before`;
}

/** The options offered when adding a lead time, in days. */
const LEAD_CHOICES = [0, 1, 3, 7, 14, 21, 28, 42, 56, 84];

const dueState = (dueDate: string) => {
  const days = daysBetween(today(), dueDate);
  if (days < 0) return { state: 'overdue' as const, days };
  if (days <= 7) return { state: 'due' as const, days };
  if (days <= 28) return { state: 'soon' as const, days };
  return { state: 'ok' as const, days };
};

/* ------------------------------------------------------------------ screen */

export default function Reminders() {
  const user = useAuth((s) => s.user);
  const isAdmin = user?.role === 'owner' || user?.role === 'admin';

  const settingsLoad = useLoad<SettingsResponse>(() => api.get('/reminders/settings'));
  const listLoad = useLoad<{ reminders: CustomReminder[] }>(() => api.get('/reminders'));

  const [editing, setEditing] = useState<CustomReminder | null>(null);
  const [adding, setAdding] = useState(false);

  const reload = () => Promise.all([settingsLoad.reload(), listLoad.reload()]);

  const { open, done } = useMemo(() => {
    const all = listLoad.data?.reminders ?? [];
    return {
      open: all.filter((r) => !r.done_at),
      done: all.filter((r) => r.done_at),
    };
  }, [listLoad.data]);

  return (
    <Screen
      onRefresh={reload}
      title="Reminders"
      subtitle="Email alerts before things fall due"
      back="/more"
      action={
        <button className="btn btn-primary btn-sm" onClick={() => setAdding(true)}>
          <Icon name="plus" size={17} />
          Add
        </button>
      }
    >
      <div className="stack-5">
        {settingsLoad.error ? <div className="notice notice-danger">{settingsLoad.error}</div> : null}
        {settingsLoad.loading && !settingsLoad.data ? <Skeleton rows={4} /> : null}

        {settingsLoad.data ? (
          <SettingsPanel
            data={settingsLoad.data}
            canEdit={isAdmin}
            onSaved={settingsLoad.reload}
          />
        ) : null}

        <div className="stack">
          <SectionHeader title="Your own reminders" count={open.length} />
          {listLoad.loading && !listLoad.data ? <Skeleton rows={3} /> : null}

          {listLoad.data && !open.length && !done.length ? (
            <EmptyState
              title="Nothing of your own yet"
              body="MOTs, inspections, tacho calibrations and plating are chased automatically. Add a reminder here for anything else — a fuel card renewal, an O-Licence disc, a service contract."
              action={
                <button className="btn btn-primary" onClick={() => setAdding(true)}>
                  Add a reminder
                </button>
              }
            />
          ) : null}

          {open.length ? (
            <div className="panel divide">
              {open.map((rem) => (
                <ReminderRow key={rem.id} reminder={rem} onOpen={() => setEditing(rem)} />
              ))}
            </div>
          ) : null}

          {done.length ? (
            <details className="stack-2">
              <summary className="hint">{done.length} done</summary>
              <div className="panel divide" style={{ marginTop: 'var(--s2)', opacity: 0.6 }}>
                {done.map((rem) => (
                  <ReminderRow key={rem.id} reminder={rem} onOpen={() => setEditing(rem)} />
                ))}
              </div>
            </details>
          ) : null}
        </div>

        {isAdmin ? <SendLog /> : null}
      </div>

      <ReminderSheet
        open={adding || editing !== null}
        reminder={editing}
        onClose={() => {
          setAdding(false);
          setEditing(null);
        }}
        onDone={() => {
          setAdding(false);
          setEditing(null);
          listLoad.reload();
        }}
      />
    </Screen>
  );
}

/* ------------------------------------------------------------------ rows */

function ReminderRow({ reminder, onOpen }: { reminder: CustomReminder; onOpen: () => void }) {
  const { state, days } = dueState(reminder.due_date);
  const identifier = reminder.reg_no || reminder.fleet_no;

  return (
    <button className="listrow" onClick={onOpen} style={{ alignItems: 'flex-start' }}>
      <span className="menu-icon">
        <Icon name={reminder.done_at ? 'check' : 'bell'} size={19} />
      </span>
      <div className="grow">
        <div className="listrow-title">{reminder.title}</div>
        <div className="listrow-sub truncate">
          {formatDate(reminder.due_date)}
          {reminder.notes ? (
            <>
              <span className="dot" />
              {reminder.notes}
            </>
          ) : null}
        </div>
        <div className="row row-tight" style={{ marginTop: 6 }}>
          {reminder.done_at ? (
            <Badge tone="neutral">Done</Badge>
          ) : (
            <StatusPill state={state} size="sm">
              {days < 0
                ? `${Math.abs(days)}d overdue`
                : days === 0
                  ? 'Today'
                  : `${days}d`}
            </StatusPill>
          )}
          {identifier ? <RegPlate reg={identifier} size="sm" /> : null}
        </div>
      </div>
      <span className="icon-chevron">
        <Icon name="chevron" size={16} />
      </span>
    </button>
  );
}

/* ------------------------------------------------------------------ settings */

function SettingsPanel({
  data,
  canEdit,
  onSaved,
}: {
  data: SettingsResponse;
  canEdit: boolean;
  onSaved: () => void;
}) {
  const { busy, error, run } = useAction();
  const [form, setForm] = useState<Settings>(data.settings);
  const [testState, setTestState] = useState<string | null>(null);
  const [addingRecipient, setAddingRecipient] = useState('');

  // A background reload (pull-to-refresh) should not clobber what is on screen.
  useEffect(() => {
    setForm(data.settings);
  }, [data.settings]);

  const save = (patch: Partial<Settings>) => {
    const next = { ...form, ...patch };
    setForm(next);
    run(async () => {
      await api.patch('/reminders/settings', next);
      onSaved();
    });
  };

  const sendTest = () =>
    run(async () => {
      setTestState(null);
      const res = await api.post<{ sentTo: string }>('/reminders/test', {});
      setTestState(`Sent to ${res.sentTo}. Check your inbox — and your spam folder.`);
    });

  const addRecipient = () => {
    const email = addingRecipient.trim().toLowerCase();
    if (!email) return;
    setAddingRecipient('');
    save({ recipients: [...new Set([...form.recipients, email])] });
  };

  const unusedLeads = LEAD_CHOICES.filter((d) => !form.leadDays.includes(d));

  return (
    <div className="stack">
      <SectionHeader title="Automatic reminders" />

      {!data.emailReady ? (
        <div className="notice notice-warn">
          Sending is not switched on for this server yet, so nothing will go out. Everything below can
          still be set up ready.
        </div>
      ) : null}
      {error ? <div className="notice notice-danger">{error}</div> : null}
      {testState ? <div className="notice notice-ok">{testState}</div> : null}

      <div className="panel divide">
        <Switch
          checked={form.enabled}
          disabled={!canEdit || busy}
          onChange={(enabled) => save({ enabled })}
          label="Email reminders"
          hint={`Sent from ${data.from} to everyone on the team.`}
        />
        <Switch
          checked={form.weeklyDigest}
          disabled={!canEdit || busy || !form.enabled}
          onChange={(weeklyDigest) => save({ weeklyDigest })}
          label="Monday summary"
          hint="One email each Monday listing everything due in the next fortnight."
        />
        <Switch
          checked={form.copyCustomer}
          disabled={!canEdit || busy || !form.enabled}
          onChange={(copyCustomer) => save({ copyCustomer })}
          label="Copy the operator"
          hint="Also email the customer's address, where one is on file."
        />
      </div>

      <div className="card card-pad stack-3">
        <div>
          <div className="listrow-title">When to send</div>
          <p className="hint" style={{ marginTop: 2 }}>
            A reminder goes out on each of these, then on the day itself, then weekly until it is
            dealt with.
          </p>
        </div>
        <div className="leadgrid">
          {form.leadDays.map((days) => (
            <span key={days} className="leadchip">
              {leadLabel(days)}
              {canEdit ? (
                <button
                  type="button"
                  aria-label={`Remove ${leadLabel(days)}`}
                  disabled={busy || form.leadDays.length === 1}
                  onClick={() => save({ leadDays: form.leadDays.filter((d) => d !== days) })}
                >
                  <Icon name="close" size={13} />
                </button>
              ) : null}
            </span>
          ))}
          {canEdit && unusedLeads.length ? (
            <select
              className="leadadd"
              value=""
              disabled={busy}
              onChange={(e) => {
                if (!e.target.value) return;
                save({ leadDays: [...form.leadDays, Number(e.target.value)].sort((a, b) => b - a) });
              }}
            >
              <option value="">+ Add</option>
              {unusedLeads.map((d) => (
                <option key={d} value={d}>
                  {leadLabel(d)}
                </option>
              ))}
            </select>
          ) : null}
        </div>
      </div>

      <div className="card card-pad stack-3">
        <div className="listrow-title">What to chase</div>
        <div className="stack-2">
          {data.availableTypes.map((type) => {
            const on = form.types.includes(type.key);
            return (
              <label key={type.key} className="picker">
                <input
                  type="checkbox"
                  checked={on}
                  disabled={!canEdit || busy}
                  onChange={() =>
                    save({
                      types: on
                        ? form.types.filter((t) => t !== type.key)
                        : [...form.types, type.key],
                    })
                  }
                />
                <span className="grow">
                  <span className="option-label">{type.label}</span>
                </span>
              </label>
            );
          })}
        </div>
      </div>

      <div className="card card-pad stack-3">
        <div>
          <div className="listrow-title">Also email</div>
          <p className="hint" style={{ marginTop: 2 }}>
            Everyone with a login already gets these. Add an office or transport manager address here.
          </p>
        </div>
        {form.recipients.length ? (
          <div className="leadgrid">
            {form.recipients.map((email) => (
              <span key={email} className="leadchip">
                {email}
                {canEdit ? (
                  <button
                    type="button"
                    aria-label={`Remove ${email}`}
                    disabled={busy}
                    onClick={() => save({ recipients: form.recipients.filter((e) => e !== email) })}
                  >
                    <Icon name="close" size={13} />
                  </button>
                ) : null}
              </span>
            ))}
          </div>
        ) : null}
        {canEdit ? (
          <div className="row row-tight">
            <input
              className="input grow"
              type="email"
              inputMode="email"
              autoCapitalize="off"
              autoCorrect="off"
              placeholder="office@example.com"
              value={addingRecipient}
              onChange={(e) => setAddingRecipient(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addRecipient();
                }
              }}
            />
            <button className="btn btn-ghost" onClick={addRecipient} disabled={busy || !addingRecipient.trim()}>
              Add
            </button>
          </div>
        ) : null}
      </div>

      {canEdit ? (
        <button className="btn btn-ghost btn-block" onClick={sendTest} disabled={busy || !data.emailReady}>
          <Icon name="mail" size={18} />
          {busy ? 'Sending…' : 'Send myself a test'}
        </button>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------ log */

function SendLog() {
  const [open, setOpen] = useState(false);
  const { data, loading, reload } = useLoad<{ sends: SendRow[] }>(() => api.get('/reminders/log'));

  return (
    <div className="stack">
      <SectionHeader
        title="Recently sent"
        action={
          <button
            className="btn btn-quiet btn-sm"
            onClick={() => {
              setOpen((o) => !o);
              if (!open) reload();
            }}
          >
            {open ? 'Hide' : 'Show'}
          </button>
        }
      />
      {open ? (
        <div className="card card-pad">
          {loading && !data ? <Skeleton rows={3} /> : null}
          {data && !data.sends.length ? (
            <p className="hint">Nothing sent yet. Reminders go out once a day, early morning.</p>
          ) : null}
          {data?.sends.length ? (
            <div className="sendlog">
              {data.sends.map((send) => (
                <div key={send.ref} className="sendlog-row">
                  <span className="sendlog-when">{send.sent_at.slice(0, 10)}</span>
                  <span className="grow">
                    <span className={send.ok ? '' : 'text-danger'}>{send.subject}</span>
                    <span className="tiny dim" style={{ display: 'block' }}>
                      {send.ok ? send.recipients : `Failed: ${send.error}`}
                    </span>
                  </span>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------ sheet */

function ReminderSheet({
  open,
  reminder,
  onClose,
  onDone,
}: {
  open: boolean;
  reminder: CustomReminder | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const { busy, error, run } = useAction();
  const [form, setForm] = useState({ title: '', notes: '', dueDate: '', assetId: '' });
  const assets = useLoad<{ assets: { id: string; reg_no: string | null; fleet_no: string | null }[] }>(
    () => api.get('/assets'),
  );

  useEffect(() => {
    if (!open) return;
    setForm({
      title: reminder?.title ?? '',
      notes: reminder?.notes ?? '',
      dueDate: reminder?.due_date ?? '',
      assetId: reminder?.asset_id ?? '',
    });
  }, [open, reminder]);

  const set = (key: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = () =>
    run(async () => {
      const body = {
        title: form.title,
        notes: form.notes || null,
        dueDate: form.dueDate,
        assetId: form.assetId || null,
      };
      if (reminder) await api.patch(`/reminders/${reminder.id}`, body);
      else await api.post('/reminders', body);
      onDone();
    });

  const markDone = () =>
    run(async () => {
      await api.patch(`/reminders/${reminder!.id}`, { done: !reminder!.done_at });
      onDone();
    });

  const remove = () =>
    run(async () => {
      await api.del(`/reminders/${reminder!.id}`);
      onDone();
    });

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={reminder ? 'Reminder' : 'New reminder'}
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={submit}
            disabled={busy || !form.title.trim() || !form.dueDate}
          >
            {busy ? 'Saving…' : reminder ? 'Save' : 'Add'}
          </button>
        </>
      }
    >
      <div className="stack-4">
        {error ? <div className="notice notice-danger">{error}</div> : null}

        <Field label="What is it">
          <input
            className="input"
            value={form.title}
            onChange={set('title')}
            placeholder="Renew O-Licence disc"
          />
        </Field>

        <Field label="Date">
          <input className="input" type="date" value={form.dueDate} onChange={set('dueDate')} />
        </Field>

        <Field label="Vehicle" hint="Optional. Links the reminder to a plate.">
          <select className="select" value={form.assetId} onChange={set('assetId')}>
            <option value="">Not vehicle specific</option>
            {assets.data?.assets.map((a) => (
              <option key={a.id} value={a.id}>
                {a.reg_no || a.fleet_no || 'Unidentified'}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Notes" hint="Included in the email.">
          <textarea className="input" rows={3} value={form.notes} onChange={set('notes')} />
        </Field>

        {reminder ? (
          <>
            <hr style={{ border: 'none', borderTop: '1px solid var(--line)' }} />
            <button className="btn btn-ghost" onClick={markDone} disabled={busy}>
              <Icon name="check" size={18} />
              {reminder.done_at ? 'Mark as still outstanding' : 'Mark as done'}
            </button>
            <button className="btn btn-danger" onClick={remove} disabled={busy}>
              Delete
            </button>
          </>
        ) : null}
      </div>
    </Sheet>
  );
}
